import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { UserCog, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Inline staff (re)assignment for an appointment row.
 * Shows current staff + click → popover with eligible candidates sorted by availability/load.
 */
export default function AssignStaffPopover({ appointmentId, currentName, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(null);
  const [saving, setSaving] = useState(false);

  async function loadEligible() {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments/${appointmentId}/eligible-staff`);
      setList(data);
    } catch (e) {
      toast.error("No pudimos cargar los profesionales");
      setList([]);
    } finally { setLoading(false); }
  }

  function onOpenChange(v) {
    setOpen(v);
    if (v && list === null) loadEligible();
  }

  async function assign(staffId) {
    setSaving(true);
    try {
      await api.put(`/appointments/${appointmentId}/staff`, { staff_id: staffId });
      toast.success(staffId ? "Profesional asignado" : "Asignación retirada");
      setOpen(false);
      setList(null);
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al asignar");
    } finally { setSaving(false); }
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-left hover:bg-slate-50 rounded-md px-2 py-1 transition group w-full"
          data-testid={`appt-assign-trigger-${appointmentId}`}
          type="button"
        >
          {currentName ? (
            <span className="text-sm">{currentName}</span>
          ) : (
            <span className="text-xs text-amber-700 italic inline-flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Sin asignar
            </span>
          )}
          <UserCog className="h-3 w-3 text-slate-300 group-hover:text-[#1E3A8A] ml-auto shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" data-testid={`appt-assign-popover-${appointmentId}`}>
        <p className="overline mb-3">Asignar profesional</p>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : list && list.length === 0 ? (
          <p className="text-xs text-slate-500 py-2">Ningún profesional ofrece este servicio.</p>
        ) : (
          <div className="space-y-1 max-h-72 overflow-auto -mx-1 px-1">
            {(list || []).map((s) => (
              <button
                key={s.id}
                type="button"
                disabled={saving || (!s.available && !s.current)}
                onClick={() => assign(s.id)}
                data-testid={`assign-option-${s.id}`}
                className={`w-full text-left px-3 py-2 rounded-md border text-sm transition flex items-center justify-between gap-3 ${
                  s.current
                    ? "border-[#1E3A8A] bg-[#1E3A8A]/5"
                    : s.available
                    ? "border-slate-200 hover:border-[#1E3A8A] hover:bg-slate-50"
                    : "border-slate-100 bg-slate-50/60 opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    {s.current && <CheckCircle2 className="h-3 w-3" style={{ color: "#1E3A8A" }} />}
                    <span className="font-medium truncate">{s.name}</span>
                  </div>
                  {s.title && <div className="text-[11px] text-slate-500 truncate">{s.title}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">{s.load} cita(s)</div>
                  {s.available ? (
                    <div className="text-[10px] text-emerald-700">Disponible</div>
                  ) : (
                    <div className="text-[10px] text-red-600">{s.reason}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
        {currentName && (
          <Button
            variant="ghost" size="sm" className="w-full mt-3 text-xs"
            disabled={saving} onClick={() => assign(null)}
            data-testid="assign-clear"
          >
            Retirar asignación
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}
